import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import { AbacRuleName, AlertLevel } from '@jupiterone/jupiter-types';
import { OrderByNode } from '@jupiterone/query-language-parser';
import { CSSProperties } from 'react';
import { Vertex } from '../types';
import { VertexEntity } from '../types/entity';
import { ITokenData } from './auth/types';

/* eslint-disable max-lines */
export type Nullable<T> = T | null | undefined;
export type OptionalExceptFor<T, TRequired extends keyof T> = Partial<T> &
  Pick<T, TRequired>;
export type OmitMultiple<T, K> = Pick<T, Exclude<keyof T, K>>;

export interface FeatureFlags {
  autocomplete: boolean;
  mfaConfiguration: boolean; // Allow users setup of Multi-Factor Authentication.
  downloadPolicies: boolean; // Enable EXPORT ZIP in Policies app.
  deferHomeSearchQueries: boolean; // Sets deferredResponse strategy as FORCE.
  questionTrendCharts: boolean; // Show metrics trend chart in Question results.
  addQuestionToDashboard: boolean; // Allow user to add QuestionChart to board.
  complianceSummary: boolean; // Show compliance summary (significant API load)
  queryPermissions: boolean; // Allow admins to configure query permissions for groups.
  enableAuthClientCreation: boolean; // Allow the auth client creation page
  streamedRuleEvaluations: boolean;
  criticalAssets: boolean; // delete once feature is released
  j1EvaluateAllQuestions: boolean;
  complianceReviews: boolean; // delete once feature is released
  queryExecutionV2: boolean;
  enableSampleData: boolean; //delete once the sample data has been verified
  policiesFullTextSearch: boolean; // delete once feature is released
  compliancev15: boolean; // delete once feature is released
  notifications: boolean; // delete once feature is released
  accountLogo: boolean; // delete once feature is released
  accountLogoSettings: boolean; // delete once feature is released
  navSearchAnywhere: boolean; // delete once feature is released
  linksToTheCommunity: boolean; // delete once feature is released
  landingPageRefresh: boolean; // delete once feature is released
  complianceV2: boolean; // delete once feature is released
}

export interface RequestContext {
  abort: () => void;
}

export interface RequestError extends Error {
  retryable: boolean;
}

export interface INotificationManagerOptions {
  notificationContainerId: string;
}

export interface INotificationManager {
  success: (options: INotificationOptions) => any;
  warning: (options: INotificationOptions) => any;
  info: (options: INotificationOptions) => any;
  error: (options: INotificationOptions) => any;
}

export interface INotificationOptions {
  message: string;
  variant?: 'success' | 'info' | 'warning' | 'error';
  autoHideDuration?: number;
}

export interface IUser {
  id: string;
  profile: IUserProfile;
  mfaSettings: IMfaSettings;
}

export interface IUserProfile {
  name: string;
  givenName: string;
  familyName: string;
  email: string;
}

export interface IMfaSecret {
  code: string;
}

export interface IMfaPreferencesInput {
  SoftwareTokenMfaSettings?: {
    Enabled: boolean;
    PreferredMfa: boolean;
  };
  // TODO: Setup SMS MFA
  SMSMfaSettings?: {
    Enabled: boolean;
    PreferredMfa: boolean;
  };
}

export interface IMfaSettings {
  settingList: string[];
  preferredMethod: string;
}

export interface IAccount {
  id: string;
  subdomain: string;
  name: string;
  type: string;
  owner: string;
  status: EAccountStatus;
  deletionDate?: string;
  logo?: string;
}

export enum EAccountStatus {
  deactivated = 'DEACTIVATED',
  active = 'ACTIVE',
  deleted = 'DELETED',
}

export interface ITrialAccount extends IAccount {
  trialEndDate: string;
  trialStartDate: string;
  trialActive: boolean;
  trialStartedBy: string;
}

export function isITrialAccount(account: IAccount): account is ITrialAccount {
  return (
    'trialEndDate' in account ||
    'trialStartDate' in account ||
    'trialActive' in account ||
    'trialStartedBy' in account
  );
}

export interface ControlUserGroup {
  comparison: 'includes';
  value: string; // group id
}

export interface ControlRule {
  'user.groups': ControlUserGroup;
}

export type ControlRules = Partial<Record<AbacRuleName, ControlRule[]>>;

// ABAC control policy
export interface ControlPolicy {
  rules: ControlRules;
}

export interface ControlPolicyObject {
  name: string;
  policy: ControlPolicy;
}

// user session rules
export type IRules = Partial<Record<AbacRuleName, boolean>>;

// user session policy
export interface IPolicy {
  rules: IRules;
}

export interface ISession extends ITokenData {
  account?: IAccount;
  accounts: IAccount[];
  policy: IPolicy;
  user: IUser;
  notificationManager: INotificationManager;

  enforce: (operation: string) => boolean;
  useAccount: (accountId: string) => void;
  logout: () => void;
}

export interface ISessionManagerOptions {
  storageKey: string;
  storage?: any;
  oauthBaseUri?: string;
  authRedirectUri?: string;
  tokenUrl?: string;
  accountsUrl?: string;
  loginUrl?: string;
  logoutUrl?: string;
  authTokenVerifyRefreshInterval?: number;
  authRefreshWindowMinutes?: number;

  onUpdateSession: (session: ISession) => void;
  onAccountSet: (accountId: string, subdomain?: string) => void;
  setItem: (key: string, val: any) => void;
  getItem: (key: string) => string;
  removeItem: (key: string) => void;
  onLogout: (url: string) => void;
  onError: (err: any) => void;
  getAuthRedirectUri: () => string;
  getOauthBaseUri: () => string;
  getLocation: () => Location;
  getLoginUrl: () => string;
  getLogoutUrl: () => string;
  getTokenUrl: () => string;
  getAccountsUrl: () => string;
  getVanityAccountId: () => string | undefined;
}

export enum EInvitationStatus {
  pending = 'AWAITING-USER-ACTION',
  accepted = 'ACCEPTED',
  rejected = 'REJECTED',
  revoked = 'REVOKED',
}

export interface IInvitation {
  id: string;
  account: string;
  accountName: string;
  email: string;
  expirationTimestamp: string;
  group: string;
  groupName: string;
  invitorUser: string;
  status: EInvitationStatus;
  inviteTimestamp?: string;
}

export interface IPaginatedResponse {
  items: any[];
  links: any;
}

export interface HeadBarConfig {
  name: string;
  icon: any;
  url: string;
  featureEntitlementName: FeatureEntitlementName;
  beta?: boolean;
}

export interface Group {
  account: string;
  id: string;
  name: string;
  type: string;
  description?: string;
  deleteProtected?: boolean;
}

export interface GroupMember {
  id: string;
  profile: any;
  type: string;
}
export interface J1QLResult {
  type: 'list' | 'table' | 'tree';
  cursor?: string;
  data: any;
}

export enum DashboardType {
  USER = 'USER',
  ACCOUNT = 'ACCOUNT',
}

export interface IAuthClientsResponse {
  items: AuthClientItem[];
}

export interface CustomIdentifyProvider {
  metadataUrl?: string;
  metadataFile?: string;
  attributeMapping?: string[];
}

export interface AuthClientItem {
  id: string;
  clientSecret?: string;
  name: string;
  allowedOAuthFlows: AllowedOAuthFlow[];
  callbackUrls: string[];
  logoutUrls: string[];
  identityProviders: AllowedIdentityProviders[];
  customIdentityProvider?: CustomIdentifyProvider;
}

export enum AllowedOAuthFlow {
  CLIENT_CREDENTIALS = 'client_credentials',
  CODE = 'code',
  IMPLICIT = 'implicit',
}

export enum AllowedIdentityProviders {
  GOOGLE = 'GOOGLE',
  FACEBOOK = 'FACEBOOK',
  LIFEOMIC = 'LIFEOMIC',
  CUSTOM = 'CUSTOM',
}

export enum IdpMappingType {
  EMAIL = 'email',
  NAME = 'name',
  PICTURE = 'picture',
}

export type PropertyFormat =
  | 'uri'
  | 'email'
  | 'ip'
  | 'ipv4'
  | 'ipv6'
  | 'hostname'
  | 'date'
  | 'date-time'
  | 'local-time'
  | 'utc-date'
  | 'iso-date'
  | 'boolean';

export enum QueryJobFileStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface QueryJobInProgress {
  status: QueryJobFileStatus.IN_PROGRESS;
}

export interface QueryJobFailed {
  status: QueryJobFileStatus.FAILED;
  error: string;
}

export interface QueryJobSuccess {
  status: QueryJobFileStatus.COMPLETED;
  url: string;
}

export type ApiKey = {
  id: string;
  name: string;
  key: string;
  dateCreated: string;
  dateExpires: string;
};

export type ApiKeyResponse = {
  items: ApiKey[];
};

export interface GenericIntegrationIconProps {
  size: number;
  style?: CSSProperties;
  className?: string;
}

export type SortDirection = 'desc' | 'asc';

export interface Field {
  key: string;
  label: string;
}

export interface PageInfo {
  endCursor?: string | null;
  hasNextPage: boolean;
  currentPage?: number;
}

export interface SortInfo {
  sortBy: string;
  sortDirection?: SortDirection;
}
interface FindingEntity extends VertexEntity {
  exception?: boolean;
  exceptionReason?: string;
}
export interface FindingResult {
  id: string;
  properties: {
    [property: string]: any;
    arn: string;
    description: string;
    confidence: number;
    id: string;
    numericSeverity?: number;
    severity?: string;
    open: boolean;
    title: string;
    webLink: string;
    assetType: string;
    createdOn: string;
  };
  entity: FindingEntity;
  level?: AlertLevel;
}
export type Reducer<T, TT = T> = (state: T) => TT;
export type OptionalReducer<T> = (state?: T) => T;

export interface FetchState<T> {
  loading: boolean;
  error: any;
  data: T | undefined;
}

export type FetchStateReducer = <T>(state: FetchState<T>) => FetchState<T>;

export type KeyMap = { [key: string]: any };
export type VertexMap = { [key: string]: Vertex };

export interface PolicyBuilderGraph {
  edges: PolicyBuilderGraphEdges;
  vertices: PolicyBuilderGraphVertices;
}

export interface PolicyBuilderGraphEdges {
  byRelationshipKey: KeyMap;
  known: any[];
}

export interface PolicyBuilderGraphVertices {
  byPolicyId: VertexMap;
  byProcedureId: VertexMap;
  byReferenceId: VertexMap;
  byTemplatePath: VertexMap;
  known: Vertex[];
}

export interface OrganizationConfig {
  companyFullName: string;
  companyShortName: string;
  companyEmailDomain: string;
  companyWebsiteURL: string;
  companyMailingAddress: string;
  contactPhoneNumber: string;
  ceoName: string;
  ceoEmail: string;
  cooName: string;
  cooEmail: string;
  ctoName: string;
  ctoEmail: string;
  securityOfficerName: string;
  securityOfficerEmail: string;
  privacyOfficerName: string;
  privacyOfficerEmail: string;
  securityCommitteeMembers: string;
  wantCustomMkdocsTemplate: boolean;
  mkdocsLogoURL: string;
  mkdocsThemeColorPrimary: string;
  mkdocsThemeColorAccent: string;
  securityPolicyURL: string;
  privacyPolicyURL: string;
  cookiePolicyURL: string;
  sourceControl: string;
  ticketingSystem: string;
  internalHelpdeskURL: string;
  cmPortal: string;
  ciSystem: string;
  hrSystem: string;
  haveSecurityScorecard: boolean;
  securityScorecardPeriod: string;
  securityScorecardURL: string;
  expenseReporting: string;
  devWikiURL: string;
  hipaaTrainingURL: string;
  securityAwarenessTrainingProvider: string;
  IdP: string;
  needStandardHIPAA: boolean;
  needStandardHITRUST: boolean;
  needStandardGDPR: boolean;
  needStandardNIST: boolean;
  needStandardPCI: boolean;
}

export interface SecurityStandardConfig {
  id: string;
  file: string;
  name: string;
  type: string;
  supported: boolean;
  adopted: boolean;
}

export interface SecurityConfig {
  id: string;
  file: string;
  name: string;
  adopted: boolean;
}

export interface PolicyConfig extends SecurityConfig {
  procedures: string[];
}

export type ResourceItem = {
  name: string;
  link: string;
};

export interface ProcedureConfig extends SecurityConfig {
  provider: string;
  summary: string;
  type?: string;
  guidance?: string;
  applicable: boolean;
  resources?: ResourceItem[];
}

export interface ReferenceConfig extends SecurityConfig {
  applicable: boolean;
  summary?: string;
  guidance?: string;
}

export type PolicyBuilderConfig = {
  organization: OrganizationConfig;
  standards: SecurityStandardConfig[];
  policies: PolicyConfig[];
  procedures: ProcedureConfig[];
  references: ReferenceConfig[];
};

export interface PolicyUpdate {
  name: string;
  content: string;
}

export interface ProcedureUpdate {
  name: string;
  content: string;
  adopted: boolean;
  summary?: string;
  provider?: string;
}

export enum PoliciesSource {
  Policies = 'policies',
  Procedures = 'procedures',
  References = 'references',
}

export interface SynchronizationJobEntity {
  _key: string;
  _type: string;
  _class: string;
  displayName: string;
  _rawData?: {
    [name: string]: {
      contentType: string;
      body: string;
    };
  };
  [property: string]: any;
}

export interface SynchronizationJobRelationship {
  _key: string;
  _type: string;
  _class: string;
  _fromEntityKey: string;
  _toEntityKey: string;
}

export interface SynchronizationJobStart {
  source: string;
  scope?: string;
  integrationInstanceId?: string;
}

export enum SynchronizationJobStatus {
  AWAITING_UPLOADS = 'AWAITING_UPLOADS',
  FINALIZE_PENDING = 'FINALIZE_PENDING',
  FINALIZING_ENTITIES = 'FINALIZING_ENTITIES',
  FINALIZING_RELATIONSHIPS = 'FINALIZING_RELATIONSHIPS',
  ABORTED = 'ABORTED',
  FINISHED = 'FINISHED',
  UNKNOWN = 'UNKNOWN',
  ERROR_BAD_DATA = 'ERROR_BAD_DATA',
  ERROR_UNEXPECTED_FAILURE = 'ERROR_UNEXPECTED_FAILURE',
}

export interface SynchronizationJobResponse {
  job: {
    source: string;
    scope?: string;
    id: string;
    status: SynchronizationJobStatus;
    startTimestamp: number;
    numEntitiesUploaded: number;
    numEntitiesCreated: number;
    numEntitiesUpdated: number;
    numEntitiesDeleted: number;
    numRelationshipsUploaded: number;
    numRelationshipsCreated: number;
    numRelationshipsUpdated: number;
    numRelationshipsDeleted: number;
  };
}

export enum QueryResultsChangeType {
  Delete = 'Delete',
}

export interface QueryResultsChange {
  data: any;
  type: QueryResultsChangeType;
}

export enum SampleDatasets {
  FULL = 'full',
}

export type SampleDatasetStatus = {
  status: SampleDatasetStates;
  datasetName: string;
};

export const sampleDatasetNames: SampleDatasets[] = [SampleDatasets.FULL];

export enum SampleDatasetStates {
  UNIMPORTED = 'UNIMPORTED',
  IMPORTING = 'IMPORTING',
  IMPORTED = 'IMPORTED',
  REMOVING = 'REMOVING',
  REMOVED = 'REMOVED',
}

export interface BasePaginationProps {
  loading: boolean;
  hasNextPage: boolean;
  loadMore: () => Promise<void>;
  totalResultCount?: number;
}

export interface LoadMoreOrderByOptions {
  orderBy?: OrderByNode;
}

export interface PaginationSortingProps extends BasePaginationProps {
  loadMoreOrderBy?: ({ orderBy }: LoadMoreOrderByOptions) => Promise<void>;
}
